// React dependencies
import React, { useState, useEffect } from 'react';

// External dependencies
import { connect } from 'react-redux';
import { Button, Grid, Dialog } from '@material-ui/core';
import { toast } from 'react-toastify';
import { DropzoneArea } from 'material-ui-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import VisibilityIcon from '@material-ui/icons/Visibility';
import IconButton from '@material-ui/core/IconButton';
import ReactPlayer from 'react-player';
import PropTypes from 'prop-types';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

// Internal dependencies
import { uploadMedia, duplicateMedia } from '../../actions/admin/advertisementAction';
import { Colours, IMAGE_FORMATS, AdType } from '../../common/constants';
import { IsArray, IsEmpty } from '../../common/checks';
import FixedCropper from '../global/image/FixedCropper';
import OverLoading from '../global/OverLoading';
import PostImages from './postImages';
import apiService from '../../services.js/apiService';

const maxImageFileSize = 2_000_000;
const maxVideoFileSize = 10_485_760;
const videoFormats = ['video/mp4'];
const excludedAdsType = [
  AdType.Post,
  AdType.Stickers,
  AdType.Page,
  AdType.Highlight,
];

const useStyles = makeStyles({
  myDropZone: {
    position:        'relative',
    width:           '50%',
    height:          '10%',
    minHeight:       '50px',
    backgroundColor: '#ffffff',
    border:          0,
    boxShadow:       '0px 0px 1px 0px rgba(0, 0, 0, 1)',
    borderRadius:    5,
    borderColor:     '#000000',
    cursor:          'pointer',
    boxSizing:       'border-box',
  },
});

const Media = (props) => {
  const [loaded, setLoaded] = useState(true);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('');
  const [currentFile, setCurrentFile] = useState(null);
  const [src, setSrc] = useState('');
  const [url, setUrl] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [images, setImages] = useState(props.images || {
    display:  null,
    original: null,
  });
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [video, setVideo] = useState(props.adVideo);
  const [showVideo, setShowVideo] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    const fetchPost = async () => {
      const post = await apiService.getPostById(props.postId);
      if (!IsEmpty(post)) {
        setPostImages(post.images);
      }
    };
    if (props.adType === AdType.Post) {
      fetchPost();
    }
  }, [props.postId]);

  const handleImageErrorMessage = (file) => {
    if (!IMAGE_FORMATS.includes(file.type)) {
      toast.error('Only JPG, JPEG and PNG files are supported.');
      return;
    }
    if (file.size > maxImageFileSize) {
      toast.error(`Maximum file size of ${maxImageFileSize / 1_000_000}MB exceeded`);
    }
  };

  const handleVideoErrorMessage = (file) => {
    if (!videoFormats.includes(file.type)) {
      toast.error('Only mp4 file is supported');
      return;
    }
    if (file.size > maxVideoFileSize) {
      toast.error(`Maximum video file size of ${maxVideoFileSize / 1_000_000}MB exceeded`);
    }
  };

  const handleClose = () => {
    setCurrentFile(null);
    setSrc('');
    setUrl(null);
    setType('');
    setOpen(false);
  };

  const saveVideo = async (file) => {
    try {
      setLoaded(false);

      if (!IsEmpty(file)) {
        const formData = new FormData();
        formData.append('video', file);
        formData.append('type', 'videos');
        const response = await props.uploadMedia(props.adId, formData);
        if (!IsEmpty(response?.video)) {
          setVideo(response.video);
          props.onChangedVideo(response.video);
          toast.success('Video successfully uploaded.');
        } else {
          toast.error('An error occured while uploading the video.');
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoaded(true);
    }
  };

  const handleUploadUrl = async (file, originalSourceUrl) => {
    try {
      const formData = new FormData();
      formData.append('cropImage', file);
      formData.append('originalSourceUrl', originalSourceUrl);

      const result = await props.duplicateMedia(props.adId, formData);
      if (!IsEmpty(result)) {
        const nextImages = {
          display:  result.display,
          original: result.original,
        };
        setImages(nextImages);
        props.onChangedImages(nextImages);

        toast.success('Image successfully uploaded');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSave = async (file) => {
    if (file) {
      if (type === 'duplicate') {
        await handleUploadUrl(file, url);
      } else {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('image', currentFile);
        try {
          const response = await props.uploadMedia(props.adId, formData);
          if (!IsEmpty(response) && !IsEmpty(response.image)) {
            const nextImages = {
              display:  response.image.display,
              original: response.image.original,
            };
            setImages(nextImages);
            props.onChangedImages(nextImages);
            setCurrentFile('');
            toast.success('Image uploaded successfully');
          }
        } catch (err) {
          toast.error(err.message);
        }
      }
    }
    setOpen(false);
  };

  const handleUpload = (files) => {
    if (!IsEmpty(files) && IsArray(files)) {
      setType('upload');
      setCurrentFile(files[0]);

      const reader = new FileReader();
      reader.addEventListener('load', () => setSrc(reader.result));
      reader.readAsDataURL(files[0]);

      setOpen(true);
      /*** */
      // Save immediately without cropping. Comment code below if we want to enable cropping.
      // handleSave(files[0]);
    }
  };

  const handleDelete = async () => {
    try {
      setLoaded(false);
      const response = await apiService.deleteAdMedia(props.adId);
      if (!IsEmpty(response)) {
        setImages({
          display:  null,
          original: null,
        });
        props.onChangedImages({ display: null, original: null });
        setLoaded(true);

        toast.success('Image successfully deleted.');
      }
    } catch (err) {
      setLoaded(true);
      toast.error(err.message);
    }
  };

  const handleDeleteVideo = async () => {
    try {
      setLoaded(false);
      const response = await apiService.deleteAdMedia(props.adId);
      if (!IsEmpty(response)) {
        setVideo('');
        props.onChangedVideo('');
        setLoaded(true);
        toast.success('Video successfully deleted.');
      }
    } catch (err) {
      setLoaded(true);
      toast.error(err.message);
    }
  };

  const handlePostImageSelected = async (imageUrl) => {
    setType('duplicate');
    setUrl(imageUrl);
    setSrc(imageUrl);
    setOpen(true);
  };

  return (
    <>
      {!loaded && <OverLoading />}
      <div>
        <Grid>
          {
            !excludedAdsType.includes(props.adType) && (
              <Grid item className="mb-5">
                <div className="alert alert-primary" role="alert">
                  You can only upload either one of the media type
                </div>
              </Grid>
            )
          }

          {
            !excludedAdsType.includes(props.adType) && (
              <>
                {
                  IsEmpty(images.display) && (
                    <>
                      <Grid item>
                        <h5>Advertisement Video</h5>
                      </Grid>

                      <Grid item style={{ marginBottom: 20 }}>
                        <hr />
                        { IsEmpty(video) ? (
                          <>
                            <h6>Upload video</h6>
                            <center>
                              <DropzoneArea
                                dropzoneText=""
                                onChange={(files) => {
                                  if (!IsEmpty(files) && IsArray(files)) {
                                    saveVideo(files[0]);
                                  }
                                }}
                                dropzoneClass={classes.myDropZone}
                                showPreviewsInDropzone={false}
                                showPreviews={false}
                                showAlerts={false}
                                getDropRejectMessage={(file) => handleVideoErrorMessage(file)}
                                filesLimit={1}
                                maxFileSize={maxVideoFileSize}
                                acceptedFiles={videoFormats}
                              />
                            </center>
                          </>
                        ) : (
                          <>
                            <h6>Uploaded video</h6>
                            <div style={{ position: 'relative', marginBottom: 100, width: '50%' }}>
                              <ReactPlayer
                                url={video}
                                width="100%"
                                style={{ boxShadow: '0 2px 4px 2px rgba(0, 0, 0, .3)' }}
                              />
                              <div className="row" style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                                <IconButton
                                  onClick={() => { setShowVideo(true); }}
                                  style={{
                                    backgroundColor: '#ffffff',
                                    boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
                                    verticalAlign:   'bottom',
                                    margin:          '10px 5px 0px 0px',
                                  }}
                                >
                                  <PlayCircleOutlineIcon />
                                </IconButton>

                                <IconButton
                                  onClick={() => { handleDeleteVideo(); }}
                                  style={{
                                    backgroundColor: '#ffffff',
                                    boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
                                    verticalAlign:   'bottom',
                                    margin:          '10px 15px 0px 0px',
                                  }}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </div>
                            </div>
                          </>
                        )}

                        <center>
                          <Dialog
                            id="videoDialog"
                            fullWidth
                            maxWidth="sm"
                            open={showVideo}
                            onClose={() => setShowVideo(false)}
                          >
                            <ReactPlayer url={video} playing loop controls />
                          </Dialog>
                        </center>
                      </Grid>
                    </>
                  )
                }
              </>
            )
          }

          {
            IsEmpty(video) && (
              <>
                <Grid item>
                  <h5>Advertisement Image</h5>
                </Grid>
                {
                  (IsEmpty(images.display) && !IsEmpty(postImages)) && (
                    <Grid item>
                      <hr />
                      <h6>Select Image from Post</h6>
                      <PostImages
                        postImages={postImages}
                        onPostImageSelected={handlePostImageSelected}
                      />
                    </Grid>
                  )
                }
                {
                  IsEmpty(images.display) && (
                    <Grid item style={{ marginBottom: 20 }}>
                      <hr />
                      <h6>Upload Image</h6>
                      <center>
                        <DropzoneArea
                          dropzoneText=""
                          onChange={handleUpload}
                          dropzoneClass={classes.myDropZone}
                          showPreviewsInDropzone={false}
                          showPreviews={false}
                          showAlerts={false}
                          getDropRejectMessage={(file) => handleImageErrorMessage(file)}
                          filesLimit={1}
                          maxFileSize={maxImageFileSize}
                          acceptedFiles={IMAGE_FORMATS}
                        />

                      </center>
                    </Grid>
                  )
                }
                {
                  !IsEmpty(images.display) && (
                    <Grid item>
                      <hr />
                      <div>
                        <h6>Uploaded Image</h6>
                        <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                          <img
                            style={{
                              borderColor: Colours.Grey,
                              borderWidth: 2,
                              borderStyle: 'solid',
                            }}
                            src={images.display}
                            alt=""
                          />
                          <div style={{
                            display:        'flex',
                            justifyContent: 'center',
                            marginTop:      10,
                          }}
                          >
                            <IconButton
                              onClick={() => { setIsViewerOpen(true); }}
                              style={{
                                backgroundColor: '#ffffff',
                                boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
                                padding:         8,
                                margin:          '0px 5px 0px 0px',
                              }}
                            >
                              <VisibilityIcon style={{ fontSize: 22 }} />
                            </IconButton>
                            <IconButton
                              onClick={() => { handleDelete(); }}
                              style={{
                                backgroundColor: '#ffffff',
                                boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
                                padding:         8,
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 22 }} />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    </Grid>
                  )
                }
              </>
            )
          }
        </Grid>

        <div
          className="d-flex"
          style={{
            justifyContent: 'flex-end',
            marginTop:      30,
          }}
        >
          {
            ![...excludedAdsType, AdType.Url].includes(props.adType)
              ? (
                <Button
                  disabled={IsEmpty(images.display) && IsEmpty(video)}
                  onClick={() => { props.onActiveStepChanged(props.activeStep + 1); }}
                  variant="contained"
                  color="default"
                >
                  Next
                </Button>
              ) : (props.adType === AdType.Url) ? (
                <>
                  <Button
                    onClick={() => { props.onActiveStepChanged(props.activeStep - 1); }}
                    variant="contained"
                    color="default"
                  >
                    Back
                  </Button>
              &nbsp;
                  <Button
                    disabled={IsEmpty(images.display) && IsEmpty(video)}
                    onClick={() => { props.onActiveStepChanged(props.activeStep + 1); }}
                    variant="contained"
                    color="default"
                  >
                    Next
                  </Button>
                </>
              )
                : (
                  <>
                    <Button
                      onClick={() => { props.onActiveStepChanged(props.activeStep - 1); }}
                      variant="contained"
                      color="default"
                    >
                      Back
                    </Button>
            &nbsp;
                    <Button
                      disabled={IsEmpty(images.display)}
                      onClick={() => { props.onActiveStepChanged(props.activeStep + 1); }}
                      variant="contained"
                      color="default"
                    >
                      Next
                    </Button>
                  </>
                )
          }
        </div>
        <Dialog
          fullScreen
          open={open}
          onClose={handleClose}
        >
          <FixedCropper
            width={16}
            height={9}
            minUploadPixelWidth={320}
            minUploadPixelHeight={180}
            cropPixelWidth={320}
            cropPixelHeight={180}
            outputPixelWidth={960}
            outputPixelHeight={540}
            type={type}
            url={url}
            src={src}
            file={currentFile}
            handleSave={handleSave}
            handleClose={handleClose}
          />
        </Dialog>

        {
          isViewerOpen && !IsEmpty(images.original) && (
            <Lightbox
              animationDisabled
              mainSrc={images.original}
              onCloseRequest={() => setIsViewerOpen(false)}
            />
          )
        }
      </div>
    </>
  );
};

Media.defaultProps = {
  images:  null,
  adVideo: null,
  postId:  null,
};

Media.propTypes = {
  activeStep:          PropTypes.number.isRequired,
  onActiveStepChanged: PropTypes.func.isRequired,
  adId:                PropTypes.string.isRequired,
  adType:              PropTypes.oneOf(Object.values(AdType)).isRequired,
  images:              PropTypes.shape({
    display:  PropTypes.string,
    original: PropTypes.string,
  }),
  adVideo:         PropTypes.string,
  postId:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChangedImages: PropTypes.func.isRequired,
  onChangedVideo:  PropTypes.func.isRequired,
  uploadMedia:     PropTypes.func.isRequired,
  duplicateMedia:  PropTypes.func.isRequired,
};

export default connect(null, { uploadMedia, duplicateMedia })(Media);
